import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './LinkShortener.css';

function LinkShortener() {
  const [url, setUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const err = searchParams.get('error');
    const notfound = searchParams.get('notfound');
    if (err === 'unavailable') setError('Link shortener is temporarily unavailable. Database may not be configured.');
    if (notfound) setError(`Short link "${notfound}" was not found.`);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShortUrl('');
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please enter a URL.');
      return;
    }
    try {
      new URL(trimmed);
    } catch {
      setError('Please enter a valid URL (e.g. https://example.com).');
      return;
    }
    if (customCode.trim() && !/^[a-zA-Z0-9]{3,20}$/.test(customCode.trim())) {
      setError('Custom code must be 3–20 letters or numbers only.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/shorten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: trimmed.startsWith('http') ? trimmed : 'https://' + trimmed,
          customCode: customCode.trim() || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to create short link.');
        return;
      }
      setShortUrl(data.shortUrl || '');
    } catch (err) {
      setError('Could not reach the server. Make sure the backend is running and DATABASE_URL is set.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shortUrl) return;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Could not copy to clipboard.');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Link Shortener - Free URL Shortener',
    description: 'Shorten long URLs with optional custom short codes. Free link shortener with redirect.',
    url: 'https://infi-tools.com/link-shortener',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web Browser',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
  };

  return (
    <div className="link-shortener-page">
      <SEO
        title="Link Shortener - Free URL Shortener | Infi Tools"
        description="Shorten long URLs with optional custom short codes. Free link shortener with instant redirect."
      />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <div className="container">
        <div className="link-shortener-header">
          <h1>Link Shortener</h1>
          <p>Paste a long URL and get a short link. Optionally set a custom code (e.g. <code>my-page</code>).</p>
        </div>

        <AdBanner adSlot="" style={{ marginBottom: '20px' }} />

        <div className="link-shortener-container">
        <section className="link-shortener-section">
          <h2>Shorten a URL</h2>
          {error && (
            <div className="link-shortener-message link-shortener-error" role="alert">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="link-shortener-form">
            <div className="form-group">
              <label htmlFor="url">Long URL</label>
              <input
                id="url"
                type="url"
                placeholder="https://example.com/very/long/url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
                autoComplete="url"
              />
            </div>
            <div className="form-group">
              <label htmlFor="customCode">Custom code (optional)</label>
              <input
                id="customCode"
                type="text"
                placeholder="e.g. my-link"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                disabled={loading}
                maxLength={20}
                autoComplete="off"
              />
              <span className="form-hint">3–20 letters or numbers. Leave blank for a random short code.</span>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Shortening…' : 'Shorten URL'}
            </button>
          </form>

          {shortUrl && (
            <div className="link-shortener-result">
              <h3>Your short link</h3>
              <div className="result-row">
                <input type="text" readOnly value={shortUrl} className="result-input" />
                <button type="button" className="btn btn-primary copy-btn" onClick={copyToClipboard}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="result-hint">Anyone visiting this link will be redirected to your long URL.</p>
            </div>
          )}
        </section>
        </div>
      </div>

      <AdBanner adSlot="" style={{ marginTop: '40px' }} />
    </div>
  );
}

export default LinkShortener;
